import {
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridPreProcessEditCellProps,
  GridRowEditStopReasons,
  GridRowModel,
  GridRowModesModel,
  useGridApiRef,
} from '@mui/x-data-grid';
import { Box, Button, CircularProgress } from '@mui/material';
import DataGrid from 'components/data-grid';
import { Option } from 'components/data-grid/data-grid-autocomplete';
import LoadingSpinner from 'components/loading-spinner';
import { useAccountState } from 'contexts/account/account-context';
import { useGet } from 'hooks/use-get';
import { MinusCircle, PlusCircle, TimerReset } from 'lucide-react';
import Mui from 'materials';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import _ from 'underscore';
import { randomUUID } from 'utils/random';
import { isValidDecimal, isValidInteger } from 'utils/validate-numbers';
import { useDataGridError } from 'views/production-systems/hooks/use-data-grid-error';
import { getFeeds } from 'views/bluviu-nutrition/api/get-feeds';
import DataGridFeedAutocomplete from 'views/bluviu-nutrition/components/data-grid-feed-autocomplete';
import { renderFeedOption } from 'views/bluviu-nutrition/components/render-feed-option';
import { useScenarioState } from 'views/bluviu-nutrition/features/scenarios/context/scenario-context';
import { ScenarioState } from 'views/bluviu-nutrition/types/scenario-state.enum';
import {
  ScenarioFeedProgram as UpdateScenarioFeedProgram,
  UpdateScenarioFeedProgramRequest,
} from 'views/bluviu-nutrition/features/scenarios/types/update-scenario-feed-program-request.type';
import { FeedOption } from 'views/bluviu-nutrition/types/feed-option.type';
import { FeedSource } from 'views/bluviu-nutrition/types/feed-source.enum';

const headerCellStyle: React.CSSProperties = {
  whiteSpace: 'normal',
  lineHeight: 1,
};

type UpdateFeedProgramFeedForm = {
  id: string; // unique identifier for the data grid
  feed: FeedOption;
  fishSizeStart: number;
  programName: string;
  price: number;
};

type Props = {
  isUpdating: boolean;
  submit: (data: UpdateScenarioFeedProgramRequest) => void;
};

const PAGE_NAME_ID = 'bluViuNutrition.availableFeeds';

const ScenarioFeedProgram: React.FC<Props> = ({ isUpdating, submit }) => {
  const intl = useIntl();
  const { errorDictionary, changeErrorDictionary, isEditing, handleEditing, hasError } = useDataGridError();
  const apiRef = useGridApiRef();
  const {
    account: { company },
  } = useAccountState();

  const {
    scenarioQuery: { data: scenario, isLoading },
  } = useScenarioState();

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const { data: feeds } = useGet(['getFeeds', company!.companyId], ({ queryKey }) => getFeeds(String(queryKey[1])), {
    enabled: !!company?.companyId,
  });

  const defaultRow: UpdateFeedProgramFeedForm = {
    feed: { id: '', label: '', feedSource: FeedSource.MANUAL, isActive: true },
    id: randomUUID(),
    programName: '',
    fishSizeStart: 0,
    price: 0,
  };

  const defaultRows: UpdateFeedProgramFeedForm[] = useMemo(() => {
    if (!scenario?.feedPrograms?.length) return [defaultRow];
    else {
      const feedPrograms: UpdateFeedProgramFeedForm[] = _.chain(scenario.feedPrograms)
        .map((program) => {
          return program.feeds.map((feed) => {
            return {
              id: randomUUID(),
              programName: program.name.trim(),
              fishSizeStart: feed.fishSizeStart,
              price: feed.price,
              feed: {
                id: feed.feedId,
                feedSource: feed.feedSource,
                label: feed.feedName,
                isActive: feed.isActive,
              },
            };
          });
        })
        .flatten()
        .value();

      return feedPrograms;
    }
  }, [scenario?.feedPrograms, feeds]);

  const [rows, setRows] = useState<UpdateFeedProgramFeedForm[]>(defaultRows);

  useEffect(() => {
    setRows(defaultRows); // memo isn't triggering state update for some reason, this forces a state update when default rows is updated
  }, [defaultRows]);

  const handleFeedProgramSubmit = () => {
    const groupedPrograms = _.groupBy(rows, (row) => row.programName.trim());

    const req: UpdateScenarioFeedProgram[] = [];
    Object.entries(groupedPrograms).forEach(([programName, programRows]) => {
      const feedProgram: UpdateScenarioFeedProgram = {
        name: programName,
        feeds: programRows.map((row) => {
          return {
            feedId: row.feed.id,
            feedSource: row.feed.feedSource,
            price: row.price,
            fishSizeStart: row.fishSizeStart,
          };
        }),
      };
      req.push(feedProgram);
    });

    const scenarioFeedProgram: UpdateScenarioFeedProgramRequest = {
      scenarioFeedPrograms: req,
    };

    submit(scenarioFeedProgram);
  };

  const feedOptions: FeedOption[] = useMemo(() => {
    if (!feeds?.length) return [];

    return feeds.map((feed) => ({ id: feed.id, label: feed.name, feedSource: feed.feedSource, isActive: feed.active }));
  }, [feeds]);

  //#region Data Grid

  // #region HANDLERS

  const processRowUpdate = (newRow: GridRowModel<UpdateFeedProgramFeedForm>) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleRowInsert = (rowId: string) => {
    setRows((prevRow) => {
      const idx = prevRow.findIndex((row) => row.id === rowId);
      const newRows = [...prevRow];
      newRows.splice(idx + 1, 0, defaultRow);
      return newRows;
    });
    handleEditing();
  };

  const handleRowDelete = (id: string) => {
    setRows(rows.filter((row) => row.id !== id));
  };

  // #endregion HANDLERS

  // #region COLUMNS

  const editable = useMemo(() => {
    if (!scenario) return false; // no scenario, not editable

    switch (scenario.state) {
      case ScenarioState.SCENARIO_RESULT_SENT: // already computed
      case ScenarioState.SUBMITTED: // currently submitted
      case ScenarioState.COMPLETED: // scenario is completed
        return false;
      default:
        return true; // all other cases are editable
    }
  }, [scenario]);

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        headerName: 'Program Name',
        field: 'programName',
        flex: 1,
        editable: editable,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = params.props.value === '';
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-programName`]: hasError });
          return { ...params.props, error: hasError };
        },
        renderHeader: (params) => {
          return <span style={headerCellStyle}>{params.colDef.headerName}</span>;
        },
      },
      {
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.label.feed` }),
        field: 'feed',
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        editable: editable,
        type: 'singleSelect',
        valueFormatter: (value: Option) => value.label,
        valueOptions: feedOptions,
        renderCell: renderFeedOption,
        renderEditCell: (params) => <DataGridFeedAutocomplete params={params} options={feedOptions} />,
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !params.props.value;
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-feed`]: hasError });
          return { ...params.props, error: hasError };
        },
        sortable: false,
      } as GridColDef<any, FeedOption, string>,
      {
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.label.fishSize` }),
        field: 'fishSizeStart',
        flex: 1,
        type: 'number',
        width: '30%',
        align: 'right',
        editable: editable,
        headerAlign: 'left',
        renderCell: (props) => {
          return <FormattedNumber value={props.row.fishSizeStart} />;
        },
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !isValidInteger(params.props.value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-sizeStart`]: hasError });
          return { ...params.props, error: hasError };
        },
        renderHeader: (params) => {
          return <span style={headerCellStyle}>{params.colDef.headerName}</span>;
        },
      },
      {
        headerName: intl.formatMessage({ id: `${PAGE_NAME_ID}.label.customerPrice` }),
        field: 'price',
        flex: 1,
        editable: editable,
        align: 'right',
        headerAlign: 'left',
        type: 'number',
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !isValidDecimal(params.props.value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-price`]: hasError });
          return { ...params.props, error: hasError };
        },
        renderCell: (props) => {
          return <FormattedNumber value={props.row.price} />;
        },
        renderHeader: (params) => {
          return <span style={headerCellStyle}>{params.colDef.headerName}</span>;
        },
      },
      {
        headerName: '',
        field: 'actions',
        flex: 0.5,
        align: 'center',
        editable: false,
        headerAlign: 'center',
        type: 'actions',
        cellClassName: 'actions',
        getActions: (params) => {
          const id = params.row.id;
          return [
            <GridActionsCellItem
              icon={<MinusCircle />}
              disabled={rows.length === 1 || !editable}
              label={intl.formatMessage({ id: `general.removeLine` })}
              onClick={() => handleRowDelete(id)}
            />,
            <GridActionsCellItem
              label={intl.formatMessage({ id: `general.addLine` })}
              icon={<PlusCircle />}
              disabled={!editable}
              onClick={() => handleRowInsert(params.row.id)}
            />,
          ];
        },
      },
    ] as GridColDef[];
  }, [rows, feedOptions, editable]);

  // #endregion COLUMNS

  // #region HANDLERS

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const disableSubmit = useMemo(() => {
    return rows.some((row) => !row.feed.id);
  }, [rows]);

  //#endregion HANDLERS

  //#endregion Data Grid

  const isEqual = useMemo(() => {
    if (defaultRows.length === rows.length) {
      const allRowsExist = rows.every((row) =>
        defaultRows.some(
          (defaultRow) =>
            row.feed.id === defaultRow.feed.id &&
            row.fishSizeStart === defaultRow.fishSizeStart &&
            row.price === defaultRow.price &&
            row.programName === defaultRow.programName
        )
      );
      if (allRowsExist) return true;
      return false;
    }
    return false;
  }, [defaultRows, rows]);

  if (isLoading) return <LoadingSpinner />;

  return (
    <Box display={'flex'} flexDirection={'column'} gap={4}>
      <Box width={'100%'}>
        <DataGrid
          hideFooter
          autoHeight
          rows={rows}
          columns={columns}
          apiRef={apiRef}
          disableColumnMenu
          getRowId={(row) => row.id}
          rowModesModel={rowModesModel}
          onRowEditStop={handleRowEditStop}
          onCellEditStop={({ field }) => {
            if (field === 'feed') return;
            handleEditing();
          }}
          processRowUpdate={processRowUpdate}
          onRowClick={({ id }) => {
            if (!isEditing) {
              const { field } = apiRef.current?.state.tabIndex.cell ?? {};
              if (field === 'feed') return;
              apiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
            }
          }}
          onRowModesModelChange={handleRowModesModelChange}
          getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
        />
      </Box>
      <Box display={'flex'} justifyContent={'space-between'}>
        <Box display={'flex'} alignItems={'center'}>
          <Mui.Tooltip title={'Reset feed program'}>
            <Mui.IconButton disabled={isEqual} onClick={() => setRows(defaultRows)}>
              <TimerReset size={22} />
            </Mui.IconButton>
          </Mui.Tooltip>
        </Box>
        <Button
          type="submit"
          size="medium"
          disabled={isUpdating || isEditing || hasError || isEqual || disableSubmit}
          startIcon={isUpdating ? <CircularProgress color="inherit" size={18} /> : null}
          onClick={() => handleFeedProgramSubmit()}>
          <FormattedMessage id="general.save" />
        </Button>
      </Box>
    </Box>
  );
};

export default React.memo(ScenarioFeedProgram);
